import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  ARMOR_TYPE_OPTIONS,
  AttachedTable,
  Proficiency, 
  ProficiencySimple,
  SKILL_OPTIONS,
  UpgradableNumber,
  WEAPON_KEYWORD_OPTIONS
} from "@dndModels";
import { 
  ABILITY_SCORES, 
} from "@dndModels/Constants";
import { DisablableOption } from "@models";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBox";
import CheckBox from "@components/input/CheckBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import { DataType, ProficiencyType } from '@dndModels/Types';

interface Props {
  proficiency: Proficiency;
  change_time: number;
  disabled: boolean;
  onChange: (proficiency: Proficiency) => void; 
  attached_table?: AttachedTable;
}

const data_type_map: any = {
  "skill": "Skill",
  "tool": "Tool",
  "weapon_keyword": "Weapon",
  "item": "Special",
  "armor_type": "Armor",
  "STR": "Saving Throw",
  "DEX": "Saving Throw",
  "CON": "Saving Throw",
  "INT": "Saving Throw",
  "WIS": "Saving Throw",
  "CHA": "Saving Throw",
  "Same as Feat": "Saving Throw"
};

export default function ProficiencyFeatureInput({ 
  proficiency,
  change_time,
  disabled,
  attached_table,
  onChange
}: Props) {
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [obj, setObj] = useState(proficiency);

  useEffect(() => {
    if (change_time !== prev_change_time) {
      setChangeTime(change_time);
    }
    // eslint-disable-next-line
  }, [change_time]);

  const extra_options: (string | DisablableOption)[] = [];
  const priority_extra_options = ["Any"];
  const extras_from_fields: any = {};
  const data_types: DataType[] = [];
  for (const type of obj.proficiency_types) {
    switch (type) {
      case "Skill": 
        extra_options.push(...SKILL_OPTIONS); 
        priority_extra_options.push("Any Skill");
      break;
      case "Tool": 
        data_types.push("tool"); 
        extras_from_fields["tool"] = ["type"];
        priority_extra_options.push("Any Tool");
      break;
      case "Armor": extra_options.push(...ARMOR_TYPE_OPTIONS); break;
      case "Weapon": extra_options.push(...WEAPON_KEYWORD_OPTIONS); break;
      case "Special": data_types.push("item"); break;
      case "Saving Throw": 
        extra_options.push(...ABILITY_SCORES); 
        priority_extra_options.push("Same as Feat");
      break;
    }
  }
  // debugger;
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 12
        }}>
        <SelectStringBox 
          name="Proficiency Type" 
          options={["Skill","Tool","Armor","Weapon","Special","Saving Throw"]}
          values={obj.proficiency_types} 
          multiple
          disabled={disabled}
          onChange={(ids: ProficiencyType[]) => {
            const obj = proficiency;
            obj.proficiency_types = ids;
            onChange(obj);
            setObj(obj);
          }} 
        />
      </Grid>
      { (data_types.length > 0 || extra_options.length > 0) &&
        <>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectObjectBox 
              name="Proficiencies" 
              data_types={data_types}
              priority_extra_options={priority_extra_options}
              extra_options={extra_options} 
              extras_from_fields={extras_from_fields}
              multiple
              // log_it
              filters={{
                "item": { item_type: { "in": ["Weapon"] } }
              }}
              values={[...obj.the_ids, ...obj.tool_types]} 
              disabled={disabled}
              // log_it
              onChangeWithDataTypes={(ids) => {
                const obj = proficiency;
                obj.the_proficiencies = [];
                // debugger;
                for (const id_obj of ids) {
                  let data_type = id_obj.data_type;
                  if (data_type) {
                    if (data_type === "tool type") {
                      obj.tool_types.push(id_obj.id);
                    } else {
                      if (data_type.includes("Any ")) {
                        data_type = data_type.substring(4);
                      }
                      const prof_simple: ProficiencySimple = {
                        proficiency_id: id_obj.id,
                        proficiency_level: 1,
                        type: data_type_map[data_type]
                      };
                      obj.the_proficiencies.push(prof_simple);
                    }
                  } else {
                    // We need to find the data_type.
                    if (SKILL_OPTIONS.find(o => o.id === id_obj.id)) {
                      data_type = "skill";
                    } else if (ARMOR_TYPE_OPTIONS.find(o => o.id === id_obj.id)) {
                      data_type = "armor_type";
                    } else if (WEAPON_KEYWORD_OPTIONS.find(o => o.id === id_obj.id)) {
                      data_type = "weapon_keyword";
                    } else {
                      data_type = id_obj.id; // This will work for Saving Throws
                    }
                    const prof_simple: ProficiencySimple = {
                      proficiency_id: id_obj.id,
                      proficiency_level: 1,
                      type: data_type_map[data_type]
                    };
                    obj.the_proficiencies.push(prof_simple);
                  }
                }
                onChange(obj);
                setObj(obj);
              }} 
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectObjectBox 
              name="Alternates" 
              data_types={data_types}
              priority_extra_options={priority_extra_options}
              extra_options={extra_options} 
              extras_from_fields={extras_from_fields}
              multiple
              filters={{
                "item": { item_type: { "in": ["Weapon"] } }
              }}
              // limit={50}
              // internal_filter
              values={obj.alternate_ids} 
              disabled={disabled}
              onChangeWithDataTypes={(ids: { id: string, data_type: string }[]) => {
                const obj = proficiency;
                obj.alternates = [];
                ids.forEach(id_obj => {
                  const prof_simple: ProficiencySimple = {
                    proficiency_id: id_obj.id,
                    proficiency_level: 1,
                    type: data_type_map[id_obj.data_type]
                  };
                  obj.alternates.push(prof_simple);
                });
                onChange(obj);
                setObj(obj);
              }} 
            />
          </Grid>
          { obj.proficiency_types.includes("Skill") && 
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <UpgradableNumberBox
                  name="Extra Bonus" 
                  value={obj.extra_bonus} 
                  disabled={disabled}
                  attached_table={attached_table}
                  onChange={(value: UpgradableNumber) => {
                    const obj = proficiency;
                    obj.extra_bonus = value;
                    onChange(obj);
                    setObj(obj);
                  }} 
                />
              </Grid>
              { obj.feature_type !== "Expertise" && 
                <>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <CheckBox 
                      name="Double" 
                      value={obj.double} 
                      disabled={disabled || obj.prof_or_exp}
                      onChange={(e: boolean) => {
                        const obj = proficiency;
                        obj.double = e;
                        onChange(obj);
                        setObj(obj);
                      }} 
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12
                    }}>
                    <CheckBox 
                      name="Or Expertise" 
                      value={obj.prof_or_exp} 
                      disabled={disabled || obj.double}
                      onChange={(e: boolean) => {
                        const obj = proficiency;
                        obj.prof_or_exp = e;
                        onChange(obj);
                        setObj(obj);
                      }} 
                    />
                  </Grid>
                </>
              }
            </>
          }
          { !obj.prof_or_exp &&
            <Grid
              size={{
                xs: 12
              }}>
              <SelectObjectBox 
                name="Suggested" 
                data_types={data_types}
                multiple
                filters={{
                  "item": { item_type: { "in": ["Weapon","Armor"] } }
                }}
                // limit={50}
                // internal_filter
                values={ obj.suggestions.map(o => { return o.proficiency_id; }) } 
                disabled={disabled}
                // log_it
                onChangeWithDataTypes={(ids: { id: string, data_type: string }[]) => {
                  const obj = proficiency;
                  obj.suggestions = [];
                  ids.forEach(id_obj => {
                    let data_type = id_obj.data_type;
                    if (data_type === "tool type") {
                      obj.tool_types.push(id_obj.id);
                    } else {
                      if (data_type.includes("Any ")) {
                        data_type = data_type.substring(4);
                      }
                      const prof_simple: ProficiencySimple = {
                        proficiency_id: id_obj.id,
                        proficiency_level: 1,
                        type: data_type_map[data_type]
                      };
                      obj.suggestions.push(prof_simple);
                    }
                  });
                  onChange(obj);
                  setObj(obj);
                }} 
              />
            </Grid>
          }
        </>
      }
      <Grid
        size={{
          xs: 12
        }}>
        <StringBox 
          name="Choice Count"
          value={`${obj.choice_count}`} 
          type="number"
          disabled={disabled}
          onBlur={(value: number) => {
            const obj = proficiency;
            obj.choice_count = +value;
            onChange(obj);
            setObj(obj);
          }} 
        />
      </Grid>
    </Grid>
  );
}
